import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  PartyPopper, 
  Briefcase, 
  Music, 
  Users, 
  Ticket, 
  QrCode, 
  BarChart, 
  Shield,
  Check,
  ArrowRight
} from 'lucide-react';
import Logo from '@/components/Logo';

export default function ExamplesLanding() {
  const useCases = [
    {
      icon: PartyPopper,
      title: "Feste di Compleanno",
      description: "Gestisci inviti e partecipanti per feste private",
      features: [
        "Inviti digitali con QR code",
        "Guest list personalizzata",
        "Controllo accessi in tempo reale",
        "Perfetto per 20-200 persone"
      ],
      color: "from-pink-500 to-rose-500",
      example: "Maria ha organizzato il suo 30° compleanno con 80 invitati. Ha usato ZonePass per inviare inviti digitali e controllare gli arrivi in tempo reale."
    },
    {
      icon: Briefcase,
      title: "Meeting Aziendali",
      description: "Eventi corporate e conferenze professionali",
      features: [
        "Registrazione partecipanti",
        "Zone multiple (sala plenaria, workshop)",
        "Statistiche presenze dettagliate",
        "Report PDF per HR"
      ],
      color: "from-blue-500 to-indigo-500",
      example: "TechCorp ha organizzato un convegno con 500 partecipanti, 3 sale e 12 workshop. ZonePass ha gestito tutto con precisione."
    },
    {
      icon: Music,
      title: "Concerti & Festival",
      description: "Eventi musicali piccoli e grandi",
      features: [
        "Biglietti a pagamento o gratuiti",
        "Accessi VIP e backstage",
        "Controllo capacità in tempo reale",
        "Prevendita e early bird"
      ],
      color: "from-purple-500 to-pink-500",
      example: "Il Rock Club ha venduto 800 biglietti in 3 giorni, gestendo VIP, standard e accessi backstage senza code."
    },
    {
      icon: Users,
      title: "Eventi Comunitari",
      description: "Sagre, mercatini, eventi pubblici",
      features: [
        "Gestione gratuita partecipanti",
        "Database contatti per marketing",
        "Statistiche di affluenza",
        "Zero costi per eventi free"
      ],
      color: "from-green-500 to-emerald-500",
      example: "La sagra del paese ha registrato 2.000 ingressi, creando un database di 1.500 contatti per eventi futuri."
    }
  ];

  const features = [
    { icon: Ticket, text: "Biglietti illimitati" },
    { icon: QrCode, text: "QR code personalizzabili" },
    { icon: BarChart, text: "Statistiche real-time" },
    { icon: Shield, text: "Sicuro e GDPR compliant" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-extrabold mb-6">
              ZonePass per Ogni Evento
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Dalla festa di compleanno al concerto da 10.000 persone
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-8 py-6">
                  Inizia Gratis
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-purple-600 text-lg px-8 py-6">
                  Accedi
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Bar */}
      <div className="bg-white border-b border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <feature.icon className="w-6 h-6 text-purple-600" />
                <span className="font-medium text-gray-900">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Use Cases */}
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Scopri Come Puoi Usarlo
          </h2>
          <p className="text-xl text-gray-600">
            ZonePass si adatta a qualsiasi tipo di evento
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {useCases.map((useCase, idx) => (
            <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
              <div className={`h-2 bg-gradient-to-r ${useCase.color}`}></div>
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-lg bg-gradient-to-r ${useCase.color}`}>
                    <useCase.icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{useCase.title}</h3>
                    <p className="text-gray-600">{useCase.description}</p>
                  </div>
                </div>

                <div className="space-y-2 mb-6">
                  {useCase.features.map((feature, fIdx) => (
                    <div key={fIdx} className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-purple-600">
                  <p className="text-sm text-gray-700 italic">
                    💡 <strong>Caso reale:</strong> {useCase.example}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pricing Hint */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white text-center">
          <h3 className="text-3xl font-bold mb-4">Prezzi Flessibili per Ogni Evento</h3>
          <p className="text-xl mb-6 text-purple-100">
            Eventi gratuiti? Nessun costo. Eventi a pagamento? Commissione minima.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
              Inizia Ora - È Gratis
            </Button>
          </Link>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Pronto a Creare il Tuo Evento?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Registrati in 2 minuti e crea il tuo primo evento oggi stesso
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-lg px-8 py-6">
              Registrati Gratis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
